import { Injectable, computed, signal } from '@angular/core';
import { Notification, NotificationResourceObject, NotificationText, NotificationTextDocument } from './notification.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { NotificationCollectionDocument, PaginatedNotifications } from './paginated-notifications.model';
import { ErrorService } from '../error';

const JSON_API_MEDIA_TYPE = 'application/vnd.api+json';
const UNREAD_SHOWN = 3;

export interface LocalStorageNotificationStatus {

  type_dismissed_until : Date, // pending_tasks

}

type LocalStorageNotificationsByModel = {

  [key:string]:{[key:number|string]:{[key:string]:LocalStorageNotificationStatus}}

  /*
    "hub":{"14":{"pending_tasks":{"type_dismissed_until":"2024-12-15 12:49:21"}}}
  */

}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  localstorageNotificationsByModel : LocalStorageNotificationsByModel = {};
  private singleNotifications = signal<Notification[]>([]);
  private unreadNotifications = signal<Notification[]>([]);
  private _topThreeUnreadNotifications = computed(() => this.unreadNotifications().slice(0, UNREAD_SHOWN));
  private _unreadCount = signal(0);


  constructor(private http: HttpClient, private errorService: ErrorService) { 
    this.clearExpiredLocalStorageNotifications();
  }

  clearData (){
    localStorage.removeItem('localNotifications');
    this.singleNotifications.set([]);
    this.unreadNotifications.set([]);
    this._unreadCount.set(0);
  }
  clearExpiredLocalStorageNotifications(){
    const timeNow = new Date().getTime();
    let storedNotificationStatuses : LocalStorageNotificationsByModel = localStorage.getItem('localNotifications') ? JSON.parse(localStorage.getItem('localNotifications')) : {};
    for (let model in storedNotificationStatuses){
      for (let identifier in storedNotificationStatuses[model]){
        for (let type in storedNotificationStatuses[model][identifier]){
          if (storedNotificationStatuses[model][identifier][type]){ // TODO - consider removing this condition;it corrected an earlier bug and it should be unnecessary
            let dismissed_until = storedNotificationStatuses[model][identifier][type].type_dismissed_until;
            if (dismissed_until){
              let dismissed_until_date = new Date (dismissed_until);
              if (dismissed_until_date.getTime() < timeNow){
                delete storedNotificationStatuses[model][identifier][type];
              }
            }
          }
        }
      }
    }
    localStorage.setItem('localNotifications',JSON.stringify(storedNotificationStatuses))
  }
  get topThreeUnreadNotifications() {
    return this._topThreeUnreadNotifications();
  };

  /** How many unread notifications the reader has, as of the last load. */
  get unreadCount(): number {
    return this._unreadCount();
  }
  getLocalNotificationStatusFromLocalStorage (model : string, identifier : string, type : string){
    if (!model || !identifier || !type ){return;};
    let storedNotificationStatuses : LocalStorageNotificationsByModel = localStorage.getItem('localNotifications') ? JSON.parse(localStorage.getItem('localNotifications')) : {};
    return storedNotificationStatuses[model]?.[identifier]?.[type];
  }
  putNotificationStatusInLocalStorage (model : string, identifier : string, type : string, status : LocalStorageNotificationStatus){
    if (!model || !identifier || !type || !status ){return;};
    let storedNotificationStatuses : LocalStorageNotificationsByModel = localStorage.getItem('localNotifications') ? JSON.parse(localStorage.getItem('localNotifications')) : {};
    if (!storedNotificationStatuses[model]){
      storedNotificationStatuses[model] = {[identifier]:{[type]:status}};
    } else if (!storedNotificationStatuses[model][identifier]){
      storedNotificationStatuses[model][identifier] = {[type]:status};
    } else {
      storedNotificationStatuses[model][identifier][type] = status;
    }
    localStorage.setItem('localNotifications',JSON.stringify(storedNotificationStatuses))
  }
  /**
   * One page of the reader's notifications, newest first, or of the unread
   * ones only. Never cached: marking and deleting change every page.
   */
  getNotifications(page: number, unreadOnly = false): Observable<PaginatedNotifications> {
    const params: Record<string, string | number> = { 'page[number]': page || 1 };
    if (unreadOnly) {
      params['filter[read]'] = 'false';
    }
    return this.http.get<NotificationCollectionDocument>('/api/v1/notifications', {
      headers: { Accept: JSON_API_MEDIA_TYPE },
      params,
    }).pipe(
      map(document => ({ ...document, data: document.data.map(resource => this.normaliseNotification(resource)) })),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * The newest unread notifications, for the navigation bell. Asked of the
   * server rather than filtered from page one, which would miss any unread
   * notification older than the newest page.
   */
  loadUnreadNotifications(): Observable<Notification[]> {
    return this.http.get<NotificationCollectionDocument>('/api/v1/notifications', {
      headers: { Accept: JSON_API_MEDIA_TYPE },
      params: { 'filter[read]': 'false', 'page[size]': UNREAD_SHOWN },
    }).pipe(
      tap(document => this._unreadCount.set(document.meta?.total ?? document.data.length)),
      map(document => document.data.map(resource => this.normaliseNotification(resource))),
      tap(notifications => this.unreadNotifications.set(notifications)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  getNotification(id: string): Observable<Notification> {
    const cachedNotification = this.getCachedNotification(id);
    if (cachedNotification) {
      return of(cachedNotification);
    }
    return this.http.get<{ data: NotificationResourceObject }>(`/api/v1/notifications/${id}`, {
      headers: { Accept: JSON_API_MEDIA_TYPE },
    }).pipe(
      map(document => this.normaliseNotification(document.data)),
      tap(notification => this.cacheNotification(notification)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  markAsRead(id: string): Observable<Notification> {
    return this.setRead(id, true);
  }

  markAsUnread(id: string): Observable<Notification> {
    return this.setRead(id, false);
  }

  /** Every unread notification of the reader's, read now. */
  markAllAsRead(): Observable<void> {
    return this.http.post<void>('/api/v1/notifications/mark-all-read', null, {
      headers: { Accept: JSON_API_MEDIA_TYPE },
    }).pipe(
      tap(() => this.refreshUnread()),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /** Deletes the notification for the reader; the server keeps it a while for support. */
  deleteNotification(id: string): Observable<void> {
    return this.http.delete<void>(`/api/v1/notifications/${id}`, {
      headers: { Accept: JSON_API_MEDIA_TYPE },
    }).pipe(
      tap(() => {
        this.singleNotifications.set(this.singleNotifications().filter(n => n.id !== id));
        this.refreshUnread();
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  private setRead(id: string, read: boolean): Observable<Notification> {
    return this.http.patch<{ data: NotificationResourceObject }>(
      `/api/v1/notifications/${id}`,
      { data: { type: 'notifications', id, attributes: { read } } },
      { headers: { Accept: JSON_API_MEDIA_TYPE, 'Content-Type': JSON_API_MEDIA_TYPE } },
    ).pipe(
      map(document => this.normaliseNotification(document.data)),
      tap(notification => {
        this.cacheNotification(notification);
        this.refreshUnread();
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  private getCachedNotification(id: string): Notification | undefined {
    return this.singleNotifications().find(n => n.id === id);
  }

  /** Holds the notification, and drops it from the bell at once if it is now read. */
  private cacheNotification(notification: Notification): void {
    this.singleNotifications.set([...this.singleNotifications().filter(n => n.id !== notification.id), notification]);
    if (notification.read) {
      this.unreadNotifications.set(this.unreadNotifications().filter(n => n.id !== notification.id));
    }
  }

  /**
   * Reloads the bell after any change: the next unread notification moves up
   * when one is read or deleted, and the count changes either way.
   */
  private refreshUnread(): void {
    this.loadUnreadNotifications().subscribe({ error: () => undefined });
  }

  private normaliseNotification(resource: NotificationResourceObject): Notification {
    const attributes = resource.attributes;
    return {
      id: resource.id,
      kind: attributes.kind,
      label: this.normaliseText(attributes.label),
      context: attributes.context ? this.normaliseText(attributes.context) : null,
      teaser: attributes.teaser,
      target: resource.relationships?.target?.data ?? null,
      read: attributes.read,
      readAt: attributes.read_at ? new Date(attributes.read_at) : null,
      createdAt: new Date(attributes.created_at),
    };
  }

  private normaliseText(text: NotificationTextDocument): NotificationText {
    return {
      text: text.text,
      translationKey: text.translation_key,
      translationScope: text.translation_scope,
    };
  }

  private handleError(errorResponse: HttpErrorResponse) {
    return throwError(() => this.errorService.normaliseHttpError(errorResponse, 'error.something_went_wrong'));
  }
}
